import React from 'react';
import { Box, IconButton, Skeleton, Stack, TablePagination, Typography } from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { SortHeader, nextSort, type SortDirection } from './SortHeader';

export interface ListColumn<R> {
  key: string;
  /** The column's name. Always drawn: a column of names with no heading is a guess. */
  header: string;
  render: (row: R) => React.ReactNode;
  /** Fixed width in px, for a column whose content has a known size. */
  width?: number;
  /** Share of the leftover space. A column of names should take one. */
  grow?: number;
  minWidth?: number;
  align?: 'left' | 'right';
  /**
   * What to sort this column by. Give it and the heading becomes a
   * `SortHeader`; leave it out where sorting would mean nothing (a button).
   * Blanks (`null`, `undefined`, `''`) always go last.
   */
  sortValue?: (row: R) => string | number | null | undefined;
  /** First click sorts largest first: right for counts, times, amounts. */
  sortStartsDescending?: boolean;
}

export interface ListSort {
  key: string;
  direction: SortDirection;
}

export interface ListSection<R> {
  /** Drawn above its rows: "AI", "People". Leave out for one plain run of rows. */
  title?: string;
  rows: R[];
}

export interface ListTableProps<R> {
  columns: ListColumn<R>[];
  /** Either `rows` or `sections`, not both. */
  rows?: R[];
  sections?: ListSection<R>[];
  getRowKey: (row: R) => string;
  onRowClick?: (row: R) => void;
  /** The row drawn as chosen, when the table drives a panel beside it. */
  selectedKey?: string | null;
  emptyMessage?: string;
  /**
   * Rows per page. Left out, the whole list is drawn — right for a queue
   * somebody scrolls, wrong for a log that grows without end.
   */
  pageSize?: number;
  /**
   * Let the reader choose the rows per page, e.g. `[10, 25, 50]`. The pager
   * then always shows under a list with rows, with the choice and the count,
   * as a page-level list of people or records has it. Left out, the pager is
   * the small one that appears only when there is a second page.
   */
  pageSizeOptions?: number[];
  /** How the rows are sorted when the table opens; `null` keeps them as given. */
  defaultSort?: ListSort | null;
  /**
   * The rows are on their way: the headings stay, and shimmering rows hold
   * the space, so the page keeps its shape and does not jump when they land.
   */
  loading?: boolean;
  /** How many shimmering rows to hold while loading. */
  loadingRows?: number;
}

type SortKey = string | number | null | undefined;

const isBlank = (value: SortKey) => value === null || value === undefined || value === '';

/** Compare two sort values; blanks go last whichever way the column runs. */
function compare(a: SortKey, b: SortKey, direction: SortDirection): number {
  if (isBlank(a) || isBlank(b)) return isBlank(a) === isBlank(b) ? 0 : isBlank(a) ? 1 : -1;
  const order = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? order : -order;
}

/**
 * A list of things, in columns, with the columns named.
 *
 * For the tables that are read a row at a time — a queue, a call list, a
 * roster — rather than scanned as a grid of figures, which is what
 * `DataTable` is for. The differences that matter: a row can be clicked and
 * can be shown as selected, and cells wrap instead of truncating, because a
 * person's name ending in "…" helps nobody.
 *
 * Give at least one column a `grow` so the table uses the width it is
 * handed; fixed widths alone leave a gap on a wide screen and clip on a
 * narrow one.
 *
 * Columns with a `sortValue` sort from their heading, the same `SortHeader`
 * every sortable table uses. Searching and filtering are the page's: narrow
 * `rows` before handing them in, with a `SearchField` or `SegmentedControl`
 * in the panel's header.
 */
export function ListTable<R>({
  columns,
  rows,
  sections,
  getRowKey,
  onRowClick,
  selectedKey,
  emptyMessage = 'Nothing here yet.',
  pageSize: givenPageSize,
  pageSizeOptions,
  defaultSort = null,
  loading = false,
  loadingRows = 6,
}: ListTableProps<R>) {
  const [page, setPage] = React.useState(0);
  const [chosenPageSize, setChosenPageSize] = React.useState<number | null>(null);
  const pageSize = chosenPageSize ?? givenPageSize ?? pageSizeOptions?.[0];
  const [sort, setSort] = React.useState<ListSort | null>(defaultSort);

  const sortColumn = sort ? columns.find((column) => column.key === sort.key && column.sortValue) : undefined;
  const sorted = (list: R[]): R[] => {
    if (!sort || !sortColumn?.sortValue) return list;
    const value = sortColumn.sortValue;
    return [...list].sort((a, b) => compare(value(a), value(b), sort.direction));
  };
  const all: ListSection<R>[] = (sections ?? [{ rows: rows ?? [] }])
    .map((group) => ({ ...group, rows: sorted(group.rows) }));
  const count = all.reduce((n, group) => n + group.rows.length, 0);

  // Back to the first page when the order or the set of rows changes: a
  // reader who sorts or searches wants to see the top of the new list.
  React.useEffect(() => { setPage(0); }, [sort, count]);

  // Paging applies to a plain list, not to sections: a page that cuts a
  // section in half reads as a bug.
  const pages = pageSize && !sections ? Math.max(1, Math.ceil(count / pageSize)) : 1;
  const current = Math.min(page, pages - 1);
  const groups: ListSection<R>[] = pageSize && !sections
    ? [{ rows: all[0].rows.slice(current * pageSize, (current + 1) * pageSize) }]
    : all;

  const cellSx = (column: ListColumn<R>) => ({
    width: column.width,
    flexGrow: column.grow ?? 0,
    flexShrink: column.width ? 0 : 1,
    flexBasis: column.width ?? 0,
    minWidth: column.minWidth ?? (column.width ? undefined : 60),
    textAlign: column.align ?? 'left',
    minHeight: 0,
  });

  // The narrowest the columns can go, gaps included (spacing 1.5 = 12px).
  // Below it the table scrolls sideways inside its panel rather than
  // pushing its last columns out of sight.
  const narrowest = columns.reduce((sum, column) => sum + (column.width ?? column.minWidth ?? 60), 0)
    + 12 * (columns.length - 1);

  // Only the rows scroll sideways; the page controls are the table's footer
  // and stay in view below them.
  return (
    <Stack spacing={0} sx={{ width: '100%' }}>
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
    <Stack spacing={0} sx={{ width: '100%', minWidth: narrowest }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={(t) => ({ pb: 0.75, borderBottom: `1px solid ${t.tokens.border.subtle}` })}
      >
        {columns.map((column) => (
          <Box key={column.key} sx={(t) => ({ ...cellSx(column), color: t.tokens.text.tertiary })}>
            {column.sortValue ? (
              <SortHeader
                label={column.header}
                numeric={column.align === 'right'}
                direction={sort?.key === column.key ? sort.direction : null}
                onClick={() => setSort(nextSort(sort, column.key, { startDescending: column.sortStartsDescending }))}
              />
            ) : (
              <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {column.header}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      {loading ? Array.from({ length: loadingRows }, (_, i) => (
        <Stack
          key={`loading-${i}`}
          direction="row"
          alignItems="center"
          spacing={1.5}
          aria-hidden
          sx={(t) => ({ py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}` })}
        >
          {columns.map((column) => (
            <Box key={column.key} sx={cellSx(column)}>
              <Skeleton variant="text" width={column.grow ? '70%' : '85%'} />
            </Box>
          ))}
        </Stack>
      )) : null}

      {!loading && count === 0 ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary, py: 2 })}>
          {emptyMessage}
        </Typography>
      ) : null}

      {!loading && groups.map((group, gi) => (
        <React.Fragment key={group.title || `g${gi}`}>
          {group.title ? (
            <Typography
              variant="overline"
              sx={(t) => ({ color: t.tokens.text.tertiary, mt: gi === 0 ? 1 : 1.75, mb: 0.25 })}
            >
              {group.title}
            </Typography>
          ) : null}

          {group.rows.map((row) => {
            const key = getRowKey(row);
            const selected = selectedKey != null && key === selectedKey;
            return (
              <Stack
                key={key}
                direction="row"
                alignItems="center"
                spacing={1.5}
                // A control inside a row is its own action. Without this, a
                // button in a cell also opened the row behind it, so every page
                // with a row button had to remember to stop the click itself.
                onClick={onRowClick ? (event: React.MouseEvent<HTMLElement>) => {
                  const hit = (event.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]');
                  if (hit && event.currentTarget.contains(hit) && hit !== event.currentTarget) return;
                  onRowClick(row);
                } : undefined}
                sx={(t) => ({
                  py: 1,
                  cursor: onRowClick ? 'pointer' : 'default',
                  borderBottom: `1px solid ${t.tokens.border.subtle}`,
                  bgcolor: selected ? t.tokens.brand.primarySoft : 'transparent',
                  '&:hover': onRowClick
                    ? { bgcolor: selected ? t.tokens.brand.primarySoft : t.tokens.surface.sunken }
                    : undefined,
                })}
              >
                {columns.map((column) => (
                  <Box key={column.key} sx={cellSx(column)}>
                    {column.render(row)}
                  </Box>
                ))}
              </Stack>
            );
          })}
        </React.Fragment>
      ))}
    </Stack>
    </Box>

      {!loading && pageSizeOptions && pageSize && !sections && count > 0 ? (
        <TablePagination
          component="div"
          count={count}
          page={current}
          onPageChange={(_event, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event) => { setChosenPageSize(parseInt(event.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={pageSizeOptions}
          sx={(t) => ({
            mt: 1,
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            '& .MuiTablePagination-toolbar': { minHeight: 48 },
          })}
        />
      ) : !loading && pages > 1 ? (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ pt: 1.25 }}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {current * (pageSize as number) + 1}–{Math.min((current + 1) * (pageSize as number), count)} of {count}
          </Typography>
          <IconButton
            size="small"
            aria-label="Previous page"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            <ChevronLeftRounded fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next page"
            disabled={current >= pages - 1}
            onClick={() => setPage(current + 1)}
          >
            <ChevronRightRounded fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}
    </Stack>
  );
}

export default ListTable;
